import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';

interface SeriesData {
  anime_name: string;
  character_count: number;
  total_ratings: number;
  average_rating: number;
  cover_image: string | null;
}

interface AnimeCharacter {
  id: number;
  character_name: string;
  anime_name: string;
  image_url: string;
  average_rating: number;
  total_ratings: number;
  description: string | null;
}

export default function AllSeriesPage() {
  const [series, setSeries] = useState<SeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredCharacter, setFeaturedCharacter] = useState<AnimeCharacter | null>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    loadSeries();
    loadFeaturedCharacter();
  }, []);

  const loadSeries = async () => {
    try {
      // Set a timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );

      const loadData = async () => {
        // Load ALL characters using pagination to bypass the 1000 row limit
        let allCharacters: any[] = [];
        let page = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data: charactersData, error: charactersError } = await supabase
            .from('anime_characters')
            .select('anime_name, image_url, average_rating, total_ratings')
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (charactersError) {
            throw new Error(`Failed to load characters: ${charactersError.message}`);
          }

          if (charactersData && charactersData.length > 0) {
            allCharacters = [...allCharacters, ...charactersData];
            page++;
            
            // If we got less than pageSize, we've reached the end
            if (charactersData.length < pageSize) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        // Load ALL series cover images using pagination
        let allSeriesData: any[] = [];
        page = 0;
        hasMore = true;

        while (hasMore) {
          const { data: seriesData, error: seriesError } = await supabase
            .from('anime_series')
            .select('series_name, cover_image_url')
            .range(page * pageSize, (page + 1) * pageSize - 1);

          if (seriesError) {
            console.error('Error loading series images:', seriesError);
            hasMore = false;
          } else if (seriesData && seriesData.length > 0) {
            allSeriesData = [...allSeriesData, ...seriesData];
            page++;
            
            if (seriesData.length < pageSize) {
              hasMore = false;
            }
          } else {
            hasMore = false;
          }
        }

        // Create a map of series names to cover images
        const seriesCoverMap = new Map<string, string>();
        if (allSeriesData) {
          allSeriesData.forEach(s => {
            if (s.series_name && s.cover_image_url) {
              seriesCoverMap.set(s.series_name, s.cover_image_url);
            }
          });
        }

        return { charactersData: allCharacters, seriesCoverMap };
      };

      const { charactersData, seriesCoverMap } = await Promise.race([
        loadData(),
        timeoutPromise
      ]) as any;

      if (charactersData && charactersData.length > 0) {
        // Group characters by anime series
        const seriesMap = new Map<string, SeriesData>();
        
        charactersData.forEach((character: any) => {
          const animeName = character.anime_name;
          
          if (seriesMap.has(animeName)) {
            const existing = seriesMap.get(animeName)!;
            existing.character_count++;
            existing.total_ratings += character.total_ratings;
            existing.average_rating = (existing.average_rating * (existing.character_count - 1) + character.average_rating) / existing.character_count;
          } else {
            const coverImage = seriesCoverMap.get(animeName) || null;
            
            seriesMap.set(animeName, {
              anime_name: animeName,
              character_count: 1,
              total_ratings: character.total_ratings,
              average_rating: character.average_rating,
              cover_image: coverImage
            });
          }
        });
        
        // Convert to array and sort alphabetically
        const seriesArray = Array.from(seriesMap.values()).sort((a, b) => 
          a.anime_name.localeCompare(b.anime_name)
        );

        setSeries(seriesArray);
      }
    } catch (err) {
      console.error('Error loading series:', err);
      setError(err instanceof Error ? err.message : 'Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedCharacter = async () => {
    try {
      // Get total count of characters with descriptions
      const { count } = await supabase
        .from('anime_characters')
        .select('*', { count: 'exact', head: true })
        .not('description', 'is', null)
        .neq('description', '');

      if (!count || count === 0) return;

      // Generate a random offset
      const randomOffset = Math.floor(Math.random() * count);

      // Fetch character at random position
      const { data } = await supabase
        .from('anime_characters')
        .select('*')
        .not('description', 'is', null)
        .neq('description', '')
        .range(randomOffset, randomOffset)
        .single();

      if (data) {
        setFeaturedCharacter(data);
      }
    } catch (err) {
      console.error('Error loading featured character:', err);
    }
  };

  const retryLoad = () => {
    setError(null);
    setLoading(true);
    loadSeries();
  };

  const filteredSeries = series.filter(s => 
    s.anime_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group series by first letter
  const groupedSeries = filteredSeries.reduce((acc, s) => {
    const firstLetter = s.anime_name.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(s);
    return acc;
  }, {} as Record<string, SeriesData[]>);

  const sortedLetters = Object.keys(groupedSeries).sort();

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'All Anime Series - Character Rating Database',
    description: `Browse ${series.length} anime series alphabetically. Rate and review characters from your favorite anime shows.`,
    url: `${siteUrl}/series`,
    numberOfItems: series.length,
    about: {
      '@type': 'Thing',
      name: 'Anime Series Collection'
    }
  };

  // Helper function to create character URL slug
  const createCharacterSlug = (char: AnimeCharacter) => {
    const animePart = char.anime_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const namePart = char.character_name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${animePart}-${namePart}-${char.id}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent mb-4"></div>
            <p className="text-gray-600">Loading anime series...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
        <Navbar />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-2xl mx-auto text-center py-20 bg-white rounded-2xl shadow-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <i className="ri-error-warning-line text-4xl text-red-500"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={retryLoad}
              className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="All Anime Series - Browse &amp; Rate Characters Alphabetically"
        description={`Explore ${series.length} anime series alphabetically. Rate characters, view statistics, and discover new anime. Complete database of anime character ratings and reviews.`}
        keywords="anime series, anime list, anime database, character ratings, anime characters, browse anime, all anime series, anime directory"
        canonical={`${siteUrl}/series`}
        jsonLd={jsonLd}
      />
      
      <Navbar />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-4">
              All Anime Series
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Browse {series.length} anime series alphabetically and rate their characters
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for an anime series..."
                  className="w-full px-6 py-4 pl-14 rounded-full border-2 border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-200 outline-none transition-all text-base"
                />
                <div className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
                  <i className="ri-search-line text-xl text-gray-400"></i>
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Alphabet Navigation */}
          {!searchQuery && (
            <div className="mb-8 flex flex-wrap justify-center gap-2">
              {sortedLetters.map(letter => (
                <a
                  key={letter}
                  href={`#letter-${letter}`}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-white shadow-sm hover:shadow-md hover:bg-gradient-to-r hover:from-pink-500 hover:to-purple-600 hover:text-white transition-all font-semibold text-gray-700 cursor-pointer"
                >
                  {letter}
                </a>
              ))}
            </div>
          )}

          {/* Series List */}
          {filteredSeries.length > 0 ? (
            <div className="space-y-12">
              {sortedLetters.map(letter => (
                <div key={letter} id={`letter-${letter}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white text-3xl font-bold shadow-lg">
                      {letter}
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {groupedSeries[letter].length} {groupedSeries[letter].length === 1 ? 'Series' : 'Series'}
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {groupedSeries[letter].map((s) => (
                      <Link
                        key={s.anime_name}
                        to={`/series/${encodeURIComponent(s.anime_name)}`}
                        className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer"
                      >
                        <div className="flex gap-4 p-6">
                          {/* Cover Image */}
                          <div className="w-24 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
                            {s.cover_image ? (
                              <img
                                src={s.cover_image}
                                alt={s.anime_name}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-pink-400">
                                <i className="ri-film-line text-5xl"></i>
                              </div>
                            )}
                          </div>

                          {/* Series Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-pink-500 group-hover:to-purple-600 group-hover:bg-clip-text transition-all">
                              {s.anime_name}
                            </h3>

                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <i className="ri-user-line text-base"></i>
                                </div>
                                <span>{s.character_count} {s.character_count === 1 ? 'character' : 'characters'}</span>
                              </div>
                              
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <i className="ri-heart-fill text-base text-pink-500"></i>
                                </div>
                                <span>{s.total_ratings} {s.total_ratings === 1 ? 'rating' : 'ratings'}</span>
                              </div>
                            </div>

                            <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pink-500 group-hover:gap-3 transition-all">
                              <span>View Characters</span>
                              <i className="ri-arrow-right-line"></i>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No Series Found</h2>
              <p className="text-gray-600 mb-6">
                We couldn't find any anime series matching "{searchQuery}"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="inline-block px-6 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              >
                Clear Search
              </button>
            </div>
          )}

          {/* Featured Character Section */}
          {featuredCharacter && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden mt-12">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Featured Character</h2>
              </div>
              <Link
                to={`/character/${createCharacterSlug(featuredCharacter)}`}
                className="block hover:bg-gray-50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row gap-6 p-6 pt-0">
                  <div className="flex-shrink-0 w-full sm:w-48">
                    <div className="aspect-[3/4] rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={featuredCharacter.image_url}
                        alt={featuredCharacter.character_name}
                        className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {featuredCharacter.character_name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      From <span className="font-semibold">{featuredCharacter.anime_name}</span>
                    </p>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center gap-1">
                        <i className="ri-heart-fill text-pink-500"></i>
                        <span className="text-sm font-semibold text-gray-900">
                          {featuredCharacter.average_rating.toFixed(1)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {featuredCharacter.total_ratings} ratings
                      </div>
                    </div>
                    {featuredCharacter.description && (
                      <div 
                        className="text-sm text-gray-700 leading-relaxed character-description-content"
                        dangerouslySetInnerHTML={{ __html: featuredCharacter.description }}
                      />
                    )}
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
      <style>{`
        .character-description-content p {
          margin-bottom: 1.5em;
        }
        .character-description-content p:last-child {
          margin-bottom: 0;
        }
        .character-description-content ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ul li {
          margin-bottom: 0.5rem;
        }
        .character-description-content ol {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin: 1rem 0;
        }
        .character-description-content ol li {
          margin-bottom: 0.5rem;
        }
      `}</style>
    </div>
  );
}
